/**
 * Perplexity returns markdown and citation markers. Everything that reaches the
 * UI goes through clean() first.
 */
export function clean(s) {
  return (s || "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/#{1,3}\s?/g, "")
    .replace(/\[(\d+)\]/g, "")
    .replace(/\n+/g, " ")
    .trim();
}

export function truncate(s, n = 80) {
  const c = clean(s);
  return c.length > n ? c.slice(0, n) + "…" : c;
}

export function formatK(n) {
  return n >= 1000 ? "€" + Math.round(n / 1000) + "K" : "€" + n;
}

/**
 * Estimated deal value. Prefers the range Claude wrote during scoring and falls
 * back to a size bracket for rows scored before that column existed.
 */
export function estimatedValue(o) {
  if (o.est_min && o.est_max) return formatK(o.est_min) + "–" + formatK(o.est_max);
  const e = o.employee_count || 100;
  if (e > 500) return "€50K–€150K";
  if (e > 200) return "€25K–€80K";
  if (e > 50) return "€10K–€40K";
  return "€5K–€20K";
}

/** Commercial angle. Same pattern: stored value wins, heuristic is the fallback. */
export function suggestedAngle(o) {
  if (o.angle) return o.angle;
  const s = (o.sector || "").toLowerCase();
  const w = (o.why_now || "").toLowerCase();
  if (/localiz|language|multiling/.test(w)) return "Localized content audit";
  if (/seo|search|organic/.test(w)) return "SEO growth audit";
  if (/fintech|saas/.test(s)) return "Growth marketing audit";
  if (/commerce/.test(s)) return "Conversion audit";
  if (/luxury|fashion/.test(s)) return "Brand communications audit";
  if (/health/.test(s)) return "Content strategy audit";
  return "Digital presence audit";
}

function chipColor(label) {
  const t = (label || "").toLowerCase();
  if (/no contact|missing|unknown|gap|risk|inactive|outdated/.test(t)) return "volt";
  if (/reachable|founder|ceo|contact|fit|verified/.test(t)) return "green";
  if (/fund|raise|series|budget|revenue|invest/.test(t)) return "volt";
  if (/expan|market|international|launch|growth|hiring/.test(t)) return "blue";
  return "gray";
}

/**
 * Evidence chips. Claude writes these during scoring; the keyword branch only
 * runs for prospects scored before the evidence_chips column existed.
 */
export function evidenceChips(o, signals = []) {
  if (Array.isArray(o.chips) && o.chips.length) {
    return o.chips.slice(0, 4).map((l) => ({ label: l, color: chipColor(l) }));
  }
  const chips = [];
  const w = (o.why_now || "").toLowerCase();
  const mine = signals.filter((s) => s.company === o.company);
  if (mine.some((s) => s.type === "funding") || /fund|raise|series/.test(w))
    chips.push({ label: "Fresh funding", color: "volt" });
  if (mine.some((s) => s.type === "hiring") || /hiring|hire/.test(w))
    chips.push({ label: "Hiring push", color: "green" });
  if (mine.some((s) => s.type === "expansion") || /expan|international|new market/.test(w))
    chips.push({ label: "Expansion signal", color: "blue" });
  if (/localiz|language|multiling/.test(w))
    chips.push({ label: "Localization gap", color: "volt" });
  if (o.contact?.email) chips.push({ label: "Reachable decision maker", color: "green" });
  if ((o.fit || o.score) >= 80) chips.push({ label: "High fit score", color: "green" });
  if (chips.length === 0) chips.push({ label: "ICP match", color: "blue" });
  return chips.slice(0, 4);
}

const VALUE_PROPS = {
  "localized content audit":
    "We help global brands turn localization gaps into measurable demand in each market",
  "seo growth audit":
    "We help fast-growing companies turn search visibility into qualified pipeline",
  "growth marketing audit":
    "We help funded teams build acquisition engines that scale as fast as they do",
  "conversion audit":
    "We help commerce brands turn the traffic they already have into revenue",
  "brand communications audit":
    "We help premium brands turn positioning work into measurable demand",
  "content strategy audit":
    "We help expert-led organisations turn authority into inbound",
};

/** Teaser shown before a real draft exists, so the promise is visible immediately. */
export function previewLine(o) {
  const name = o.contact?.first_name || "there";
  const company = (o.company || "").trim();
  // Strip the company name first. why_now normally opens with it ("Toduba just closed…",
  // "GIMBE — launched…") and the template prints it already. Order matters: stripping
  // before the sentence split is what stops "GIMBE — launched X" collapsing to "GIMBE".
  let text = clean(o.why_now).trim();
  if (company && text.toLowerCase().startsWith(company.toLowerCase()))
    text = text.slice(company.length).replace(/^[\s\-–—,:]+/, "");
  // Then the first sentence only — the period inside "€3.5M" is not a sentence end.
  const observation = text
    .split(/[—;]|\.(?!\d)/)[0]
    .trim()
    .replace(/^[A-Z]/, (c) => c.toLowerCase());
  const vp =
    VALUE_PROPS[suggestedAngle(o).toLowerCase()] ||
    "We help growing companies turn digital gaps into measurable pipeline";
  // No usable observation (why_now empty, or nothing left once the company name is
  // stripped) means the "I noticed X …" clause has no object. Drop the clause rather
  // than emit "I noticed Casavo ." or repeat the name back as "Sifflet sifflet".
  const opener = observation
    ? `Hi ${name}, I noticed ${company} ${truncate(observation, 120)}.`
    : `Hi ${name}, I have been looking at ${company}.`;
  return `${opener} ${vp}…`;
}

export function scoreColor(score) {
  if (score >= 80) return "var(--green)";
  if (score >= 60) return "var(--volt)";
  return "var(--t3)";
}

export function fitLabel(score) {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  return "Fair";
}

export function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
